/**
 * useDemoNavigate.ts — Demo-aware navigation hook
 *
 * Wraps useNavigate so that all internal navigate() calls automatically
 * preserve demo query params (?demo=1 and ?partner=pharmacy where applicable).
 *
 * Usage:
 *   const navigate = useDemoNavigate();
 *   navigate('/nutrition'); // becomes /nutrition?demo=1&partner=pharmacy in pharmacy demo
 *
 * Falls back to standard navigate() behaviour for:
 *   - navigate(-1) — back navigation (no params to append)
 *   - External URLs
 *   - Non-demo mode
 *
 * Place at: src/hooks/useDemoNavigate.ts
 */

import { useCallback } from 'react';
import { useNavigate, type NavigateOptions } from 'react-router-dom';
import { isDemoMode, isPharmacyDemo } from '../demo';

export function useDemoNavigate() {
  const navigate = useNavigate();

  return useCallback(
    (to: string | number, options?: NavigateOptions) => {
      // Back navigation or non-demo mode — pass through unchanged
      if (typeof to === 'number' || !isDemoMode()) {
        return navigate(to as any, options);
      }

      // Build demo params
      const url = new URL(to, window.location.origin);
      url.searchParams.set('demo', '1');
      if (isPharmacyDemo()) {
        url.searchParams.set('partner', 'pharmacy');
      }

      return navigate(`${url.pathname}?${url.searchParams.toString()}`, options);
    },
    [navigate],
  );
}
